import { ANIMATION } from '@alto/design-library-tokens';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Animated, Platform, useWindowDimensions } from 'react-native';
import Modal from 'react-native-modal';
import styled from 'styled-components/native';
import { v4 as uuidv4 } from 'uuid';
import { Constants, useScreenSize } from '../../utils';
import { Column } from '../containers';

const isTest = !!process?.env?.JEST_WORKER_ID;

type NavigationStackComponent = React.ReactElement;

type SetActiveActionSheetOptions = {
  shouldRetainPreviousActionSheet?: boolean;
  dismissible?: boolean;
};

type ActionSheetProviderValue = {
  setActiveActionSheet: (
    ActionSheet: NavigationStackComponent | undefined,
    options?: SetActiveActionSheetOptions,
  ) => void;
  closeActionSheet: () => void;
  overrideActionSheetStack: (newStack: NavigationStackComponent[]) => void;
  goToPreviousActionSheet: () => void;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  restoreStack: <T extends string>(cacheKey: T) => void;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  saveStack: <T extends string>(cacheKey: T) => void;
};

export const ActionSheetContext = React.createContext<ActionSheetProviderValue>({
  setActiveActionSheet: () => null,
  closeActionSheet: () => null,
  overrideActionSheetStack: () => null,
  goToPreviousActionSheet: () => null,
  restoreStack: () => null,
  saveStack: () => null,
});

type InternalActionSheetPropsProviderValue = {
  createAnalyticsEvent: (actionSheetName: string) => void;
  handleBack?: () => void;
  handleClose: () => void;
  isFirstSheet?: boolean;
  dismissible?: boolean;
};

export const INTERNAL_ActionSheetPropsContext = React.createContext<InternalActionSheetPropsProviderValue>({
  createAnalyticsEvent: () => null,
  handleClose: () => null,
});

type ActionSheetProviderProps = {
  readonly children: React.ReactNode;
  readonly createAnalyticsEvent: (actionSheetName: string) => void;
};

type NavigationStackItem = {
  actionSheetComponent: NavigationStackComponent;
  managedProps?: InternalActionSheetPropsProviderValue;
  uuid: string;
  shouldRetainRender: boolean;
};

const StyledModal = styled(Modal)`
  justify-content: flex-end;
  margin: 0;
`;

const StyledVisibilityHack = styled(Column)<{ isHidden: boolean; isMDScreenOrBigger: boolean }>`
  max-height: 90%;
  justify-content: flex-end;
  align-self: flex-end;
  width: 100%;
  ${({ isMDScreenOrBigger }) => {
    if (Platform.OS === 'web' && isMDScreenOrBigger) {
      return 'margin: auto;';
    }
    return '';
  }}
  ${({ isHidden }) =>
    isHidden &&
    `
    opacity: 0;
    z-index: -1;
    height: 0;
  `};
`;

const modalPropsMobile: Partial<React.ComponentProps<typeof Modal>> = {
  animationIn: 'slideInUp',
  animationOut: 'slideOutDown',
  animationInTiming: isTest ? 0 : ANIMATION.SPEEDS.DEFAULT,
  animationOutTiming: isTest ? 0 : ANIMATION.SPEEDS.SLOWEST,
  backdropTransitionInTiming: 0,
  backdropTransitionOutTiming: isTest ? 0 : ANIMATION.SPEEDS.SLOWEST,
  // Ensure backdrop stays visible during close animation
  backdropOpacity: 0.5,
};

const modalPropsDesktopWeb: Partial<React.ComponentProps<typeof Modal>> = {
  animationIn: 'fadeIn',
  animationOut: 'fadeOut',
  animationInTiming: isTest ? 0 : undefined,
  animationOutTiming: isTest ? 0 : ANIMATION.SPEEDS.SLOWEST,
  backdropTransitionInTiming: 0,
  backdropTransitionOutTiming: isTest ? 0 : ANIMATION.SPEEDS.SLOWEST,
  // Ensure backdrop stays visible during close animation
  backdropOpacity: 0.5,
};

export const ActionSheetProvider = ({ children, createAnalyticsEvent }: ActionSheetProviderProps) => {
  const { width, height } = useWindowDimensions();
  const [navigationStack, setNavigationStack] = useState<NavigationStackItem[]>([]);
  // Used for reading the navigationStack without triggering a re-render.
  const virtualizedNavigationStack = useRef<NavigationStackItem[]>(navigationStack);
  virtualizedNavigationStack.current = navigationStack;
  const cache = useRef<Record<string, NavigationStackItem[]>>({});
  const { isMDScreenOrBigger } = useScreenSize();
  const [isContentReady, setIsContentReady] = useState(false);
  const contentOpacityAnim = useRef(new Animated.Value(isTest ? 1 : 0)).current;
  const isFirstSheetRef = useRef(true);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const { actionSheetComponent, managedProps } = navigationStack[navigationStack.length - 1] || {};
  const context = useMemo(
    () => managedProps || { handleClose: () => null, createAnalyticsEvent: () => null },
    [managedProps],
  );

  const handleTransition = useCallback((newStackSetter: Parameters<typeof setNavigationStack>[0]) => {
    // skip animations in tests
    if (isTest) {
      setNavigationStack(newStackSetter);
      return;
    }

    Animated.timing(new Animated.Value(1), {
      toValue: 0,
      duration: ANIMATION.SPEEDS.FAST,
      useNativeDriver: Constants.useNativeDriver,
    }).start(() => {
      setNavigationStack(newStackSetter);
    });
  }, []);

  const handleClose = useCallback(() => {
    // Start closing animation - content will be cleared in onModalHide
    setIsModalVisible(false);
  }, []);

  const handleBack = useCallback(() => {
    // If this is the last item in the stack, close the modal instead of going back
    if (virtualizedNavigationStack.current.length <= 1) {
      // Start closing animation - keep modal mounted with isVisible=false
      // so backdrop can animate out properly. Stack will be cleared in onModalHide
      setIsModalVisible(false);
      if (isTest) {
        // In tests, clear immediately without waiting for animation
        setIsContentReady(false);
        contentOpacityAnim.setValue(0);
        setNavigationStack([]);
      }
      // Don't clear stack here - let onModalHide do it after animation completes
      return;
    }
    handleTransition((prevStack) => prevStack.slice(0, prevStack.length - 1));
  }, [handleTransition, contentOpacityAnim]);

  const getManagedProps = useCallback(
    (isFirstSheet: boolean, dismissible?: boolean) => ({
      handleBack: isFirstSheet ? undefined : handleBack,
      handleClose,
      createAnalyticsEvent,
      isFirstSheet,
      dismissible,
    }),
    [handleBack, handleClose, createAnalyticsEvent],
  );

  const handleSetActiveActionSheet = useCallback(
    (NextActionSheetComponent: NavigationStackComponent | undefined, options?: SetActiveActionSheetOptions) => {
      if (!NextActionSheetComponent) {
        // Start closing animation - keep content visible during animation
        // Stack will be cleared in onModalHide after animation completes
        setIsModalVisible(false);
        isFirstSheetRef.current = true;
        // In tests, clear immediately without waiting for animation
        if (isTest) {
          setIsContentReady(false);
          contentOpacityAnim.setValue(0);
          setIsModalVisible(false);
          setNavigationStack([]);
        }
        // Don't clear stack immediately - let onModalHide do it after animation
        return;
      }

      const willBeFirstSheet = virtualizedNavigationStack.current.length === 0;
      if (willBeFirstSheet) {
        if (isTest) {
          // In tests, render content immediately with full opacity
          setIsContentReady(true);
          contentOpacityAnim.setValue(1);
          isFirstSheetRef.current = false;
        } else {
          setIsContentReady(false);
          contentOpacityAnim.setValue(0);
          isFirstSheetRef.current = true;
        }
        setIsModalVisible(true);
      } else {
        // Modal already visible, content can render immediately with full opacity
        setIsContentReady(true);
        contentOpacityAnim.setValue(1);
        // Ensure modal is visible even if it was closed (e.g., in tests after closing previous sheet)
        if (!isModalVisible) {
          setIsModalVisible(true);
        }
      }

      handleTransition((prevStack) => {
        const nextNavStackItem = {
          actionSheetComponent: NextActionSheetComponent,
          managedProps: getManagedProps(willBeFirstSheet, options?.dismissible),
          uuid: uuidv4(),
          shouldRetainRender: false,
        };
        if (options?.shouldRetainPreviousActionSheet && prevStack.length > 0) {
          // Only retain previous sheet if there actually is one
          const previousNavStackItem = {
            ...prevStack[prevStack.length - 1],
            shouldRetainRender: true,
          };
          const restOfStack = prevStack.slice(0, prevStack.length - 1);
          return [...restOfStack, previousNavStackItem, nextNavStackItem];
        }
        return [...prevStack, nextNavStackItem];
      });
    },
    [handleTransition, contentOpacityAnim, isModalVisible, getManagedProps],
  );

  const overrideActionSheetStack = useCallback(
    (newStack: NavigationStackComponent[]) => {
      const internalStack = newStack.map((stackItem, i) => ({
        actionSheetComponent: stackItem,
        managedProps: getManagedProps(i === 0),
        uuid: uuidv4(),
        shouldRetainRender: false,
      }));

      const willBeFirstSheet = virtualizedNavigationStack.current.length === 0;
      if (willBeFirstSheet) {
        if (isTest) {
          // In tests, render content immediately with full opacity
          setIsContentReady(true);
          contentOpacityAnim.setValue(1);
          isFirstSheetRef.current = false;
        } else {
          setIsContentReady(false);
          contentOpacityAnim.setValue(0);
          isFirstSheetRef.current = true;
        }
        setIsModalVisible(true);
      } else {
        // Modal already visible, content can render immediately with full opacity
        setIsContentReady(true);
        contentOpacityAnim.setValue(1);
        // Ensure modal is visible even if it was closed (e.g., in tests after closing previous sheet)
        if (!isModalVisible) {
          setIsModalVisible(true);
        }
      }

      handleTransition(internalStack);
    },
    [getManagedProps, handleTransition, contentOpacityAnim, isModalVisible],
  );

  const goToPreviousActionSheet = useCallback(() => {
    handleBack();
  }, [handleBack]);

  const closeActionSheet = useCallback(() => {
    handleSetActiveActionSheet(undefined);
  }, [handleSetActiveActionSheet]);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  const saveStack = <T extends string>(cacheKey: T) => {
    cache.current[cacheKey] = virtualizedNavigationStack.current;
  };
  const saveStackMemoized = useCallback(saveStack, []);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  const restoreStack = <T extends string>(cacheKey: T) => {
    setNavigationStack(cache.current[cacheKey] ?? []);
    cache.current[cacheKey] = [];
  };
  const restoreStackMemoized = useCallback(restoreStack, []);

  const memoizedValue = useMemo(
    () => ({
      setActiveActionSheet: handleSetActiveActionSheet,
      overrideActionSheetStack,
      closeActionSheet,
      goToPreviousActionSheet,
      restoreStack: restoreStackMemoized,
      saveStack: saveStackMemoized,
      isFirstSheet: virtualizedNavigationStack.current.length === 0,
    }),
    [
      handleSetActiveActionSheet,
      closeActionSheet,
      overrideActionSheetStack,
      goToPreviousActionSheet,
      restoreStackMemoized,
      saveStackMemoized,
    ],
  );

  const modalProps = isMDScreenOrBigger ? modalPropsDesktopWeb : modalPropsMobile;

  const handleModalWillShow = useCallback(() => {
    // Skip animations in tests - render content immediately
    if (isTest) {
      setIsContentReady(true);
      contentOpacityAnim.setValue(1);
      isFirstSheetRef.current = false;
      return;
    }

    // On native, render content immediately so it's part of the slideInUp animation
    // but keep it invisible initially to prevent flicker, then fade it in
    if (isFirstSheetRef.current) {
      if (Platform.OS === 'web') {
        // On web, one frame delay is sufficient for fadeIn
        requestAnimationFrame(() => {
          setIsContentReady(true);
          Animated.timing(contentOpacityAnim, {
            toValue: 1,
            duration: 0,
            useNativeDriver: true,
          }).start();
          isFirstSheetRef.current = false;
        });
      } else {
        // On native, render content immediately (so it slides with modal) but start invisible
        setIsContentReady(true);
        // Fade in content after a short delay to prevent flicker while still showing slide
        setTimeout(() => {
          Animated.timing(contentOpacityAnim, {
            toValue: 1,
            duration: 150,
            useNativeDriver: true,
          }).start();
          isFirstSheetRef.current = false;
        }, 50); // 50ms delay - content is already sliding, just becomes visible
      }
    }
  }, [contentOpacityAnim]);

  const handleModalHide = useCallback(() => {
    // Hide content and clear stack after modal animation fully completes
    // Content is kept visible during the close animation (in handleModalWillHide we do nothing)
    // to prevent flickering. This is especially important for web fadeOut animations.
    // Note: Backdrop fades out with backdropTransitionOutTiming which matches
    // the modal animation timing to prevent flicker
    setIsContentReady(false);
    contentOpacityAnim.setValue(0);
    setIsModalVisible(false);
    // Clear the navigation stack after animation completes
    setNavigationStack([]);
  }, [contentOpacityAnim]);

  const { dismissible, handleClose: managedHandleClose } = managedProps || {};
  const handleBackdropPress = useCallback(() => {
    if (dismissible !== false) {
      managedHandleClose?.();
    }
  }, [dismissible, managedHandleClose]);

  return (
    <ActionSheetContext.Provider value={memoizedValue}>
      {children}
      <INTERNAL_ActionSheetPropsContext.Provider value={context}>
        {(!!actionSheetComponent || isModalVisible) && (
          <StyledModal
            {...(modalProps as React.ComponentProps<typeof Modal>)}
            useNativeDriver={false}
            avoidKeyboard
            isVisible={isModalVisible}
            onModalWillShow={handleModalWillShow}
            onModalHide={handleModalHide}
            onBackdropPress={handleBackdropPress}
            onSwipeComplete={managedProps?.handleClose ?? (() => {})}
            propagateSwipe // allow children to receive swipe events (i.e. scroll)
            swipeDirection="down"
            deviceHeight={height}
            deviceWidth={width}
            useNativeDriverForBackdrop={Platform.OS === 'android'} // avoid backdrop flickering on android
            // eslint-disable-next-line react-native/no-inline-styles
            style={{ margin: 0 }} // ensure modal fills entire screen including bottom navigation
          >
            {isContentReady &&
              navigationStack
                .map((actionSheetObject, index) => {
                  if (
                    !actionSheetObject.actionSheetComponent ||
                    (!actionSheetObject.shouldRetainRender && index !== navigationStack.length - 1)
                  ) {
                    return null;
                  }
                  return (
                    <Animated.View
                      key={actionSheetObject.uuid || actionSheetObject.actionSheetComponent.key}
                      // eslint-disable-next-line react-native/no-inline-styles
                      style={{ opacity: contentOpacityAnim, flex: 1, justifyContent: 'flex-end' }}
                    >
                      <StyledVisibilityHack
                        flexGrow={1}
                        flexShrink={1}
                        // eslint-disable-next-line react-native/no-inline-styles
                        style={{ margin: 0 }}
                        isHidden={index !== navigationStack.length - 1}
                        isMDScreenOrBigger={isMDScreenOrBigger}
                      >
                        {actionSheetObject.actionSheetComponent}
                      </StyledVisibilityHack>
                    </Animated.View>
                  );
                })
                .filter(Boolean)}
          </StyledModal>
        )}
      </INTERNAL_ActionSheetPropsContext.Provider>
    </ActionSheetContext.Provider>
  );
};
